import {createAuthenticatedApiClient} from './apiClient';
import {stripTenantFromPayload} from '@utils/tenant';
import {inventoryProducts as sampleInventory} from '@db/inventory';
import {getApiErrorMessage, isNetworkOrOfflineError} from '@utils/apiErrorMessage';
import {isDateBeforeToday} from '@utils/formValidation';
import {normalizeMeasurementUnit} from '@constants/measurementUnits';
import {ALL_CAMPO_KEYS} from '@constants/inventoryFields';

const STORAGE_KEY = 'inventory_products';
const TYPES_STORAGE_KEY = 'inventory_types';

const inventoryAPI = createAuthenticatedApiClient();

const getStoredInventory = () => {
    const raw = localStorage.getItem(STORAGE_KEY);

    if (!raw) {
        localStorage.setItem(STORAGE_KEY, JSON.stringify(sampleInventory));
        return [...sampleInventory];
    }

    try {
        const parsed = JSON.parse(raw);
        return Array.isArray(parsed) ? parsed : [...sampleInventory];
    } catch (error) {
        return [...sampleInventory];
    }
};

const setStoredInventory = rows => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(rows));
};

const sampleTypes = [
    {id: 1, nombre: 'Equipo medico', campos: null, limite_recompra: 0},
    {id: 2, nombre: 'Reactivo', campos: null, limite_recompra: 0},
    {id: 3, nombre: 'Consumible', campos: null, limite_recompra: 0}
];

const normalizeType = type => ({
    id: type.id,
    nombre: type.nombre || '',
    campos: Array.isArray(type.campos) ? type.campos : null,
    limite_recompra: (type.limite_recompra ?? type.limiteRecompra) === null || (type.limite_recompra ?? type.limiteRecompra) === undefined || (type.limite_recompra ?? type.limiteRecompra) === ''
        ? 0
        : Number(type.limite_recompra ?? type.limiteRecompra)
});

const getStoredTypes = () => {
    const raw = localStorage.getItem(TYPES_STORAGE_KEY);

    if (!raw) {
        localStorage.setItem(TYPES_STORAGE_KEY, JSON.stringify(sampleTypes));
        return [...sampleTypes];
    }

    try {
        const parsed = JSON.parse(raw);
        return Array.isArray(parsed) ? parsed : [...sampleTypes];
    } catch (error) {
        return [...sampleTypes];
    }
};

const setStoredTypes = rows => {
    localStorage.setItem(TYPES_STORAGE_KEY, JSON.stringify(rows));
};

const normalizeProveedor = proveedor => {
    if (!proveedor || typeof proveedor !== 'object') {
        return null;
    }

    const id = proveedor.id;
    if (id === undefined || id === null || id === '') {
        return null;
    }

    return {
        id,
        nombre: proveedor.nombre || '',
        precio_unitario: proveedor.precio_unitario === null || proveedor.precio_unitario === undefined || proveedor.precio_unitario === ''
            ? null
            : Number(proveedor.precio_unitario ?? proveedor.precio ?? 0),
        cantidad: proveedor.cantidad === null || proveedor.cantidad === undefined || proveedor.cantidad === ''
            ? null
            : Number(proveedor.cantidad),
        unidad_medida: proveedor.unidad_medida
            ? normalizeMeasurementUnit(proveedor.unidad_medida)
            : null
    };
};

const normalizeProveedores = proveedores => {
    if (!Array.isArray(proveedores)) {
        return [];
    }

    return proveedores
        .map(normalizeProveedor)
        .filter(Boolean);
};

const normalizeLote = lote => ({
    id: lote?.id ?? lote?._id ?? null,
    cantidad: Number(lote?.cantidad ?? lote?.qty ?? 0),
    fecha_caducidad: lote?.fecha_caducidad ? String(lote.fecha_caducidad).slice(0, 10) : null,
    numero_lote: lote?.numero_lote ?? lote?.numeroLote ?? null,
    compra_item_id: lote?.compra_item_id ?? lote?.compraItemId ?? null
});

const normalizeLotes = lotes => (
    Array.isArray(lotes)
        ? lotes.map(normalizeLote)
        : []
);

/** Une proveedores[] y proveedor principal de GET /inventario/:id (sin duplicar por id). */
export const mergeProductProveedores = (product, extraProveedores = []) => {
    const byId = new Map();

    const add = row => {
        const normalized = normalizeProveedor(row);
        if (!normalized) {
            return;
        }

        const key = String(normalized.id);
        const existing = byId.get(key);

        if (!existing) {
            byId.set(key, normalized);
            return;
        }

        byId.set(key, {
            ...existing,
            nombre: normalized.nombre || existing.nombre,
            precio_unitario: normalized.precio_unitario ?? existing.precio_unitario,
            cantidad: normalized.cantidad ?? existing.cantidad,
            unidad_medida: normalized.unidad_medida ?? existing.unidad_medida
        });
    };

    (Array.isArray(product?.proveedores) ? product.proveedores : []).forEach(add);
    if (product?.proveedor) {
        add(product.proveedor);
    }
    (Array.isArray(extraProveedores) ? extraProveedores : []).forEach(add);

    return [...byId.values()].sort((a, b) =>
        String(a.nombre || '').localeCompare(String(b.nombre || ''), 'es', {sensitivity: 'base'})
    );
};

/** El tipo de inventario incluye el campo "tiene_fecha_caducidad" en su configuración. */
export const inventoryTypeIncludesCaducidad = tipo => {
    if (!tipo) {
        return false;
    }

    const campos = Array.isArray(tipo.campos) ? tipo.campos : null;

    if (Array.isArray(campos) && campos.length > 0) {
        return campos.includes('tiene_fecha_caducidad');
    }

    if (campos === null) {
        return ALL_CAMPO_KEYS.includes('tiene_fecha_caducidad');
    }

    return false;
};

/**
 * Indica si al comprar/ingresar stock se debe capturar caducidad por lote.
 * Usa flag del ítem, fecha en producto, campos del tipo o catálogo de tipos.
 */
export const resolveInventoryRequiresExpiry = (item, types = []) => {
    if (!item) {
        return false;
    }

    if (Boolean(item.tiene_fecha_caducidad ?? item.tieneFechaCaducidad)) {
        return true;
    }

    if (item.fecha_caducidad) {
        return true;
    }

    if (inventoryTypeIncludesCaducidad(item.tipo)) {
        return true;
    }

    const tipoId = item.tipo_id || item.tipo?.id;
    if (tipoId && Array.isArray(types) && types.length) {
        const matched = types.find(t => String(t.id) === String(tipoId));
        if (inventoryTypeIncludesCaducidad(matched)) {
            return true;
        }
    }

    return false;
};

const normalizeItem = item => ({
    id: item.id,
    nombre: item.nombre || item.modelo || '',
    modelo: item.modelo || '',
    tipo_id: Number(item.tipo_id || item.tipo?.id || 0),
    tipo: item.tipo
        ? {
            id: item.tipo.id,
            nombre: item.tipo.nombre,
            campos: Array.isArray(item.tipo.campos) ? item.tipo.campos : (item.tipo.campos === null ? null : undefined)
        }
        : null,
    tiene_fecha_caducidad: resolveInventoryRequiresExpiry(item),
    proveedor_id: item.proveedor_id === null || item.proveedor_id === undefined || item.proveedor_id === ''
        ? ''
        : Number(item.proveedor_id || item.proveedor?.id || 0) || '',
    proveedor: normalizeProveedor(item.proveedor),
    proveedores: normalizeProveedores(item.proveedores),
    configuracion: item.configuracion || '',
    cantidad: Number(item.cantidad || 0),
    limite_recompra: item.limite_recompra === null || item.limite_recompra === undefined || item.limite_recompra === ''
        ? ''
        : Number(item.limite_recompra),
    precio_por_unidad: item.precio_por_unidad === null || item.precio_por_unidad === undefined || item.precio_por_unidad === ''
        ? ''
        : Number(item.precio_por_unidad),
    idioma: item.idioma || 'Espanol',
    ultima_calibracion: item.ultima_calibracion ? String(item.ultima_calibracion).slice(0, 10) : '',
    fecha_caducidad: item.fecha_caducidad ? String(item.fecha_caducidad).slice(0, 10) : '',
    lotes: normalizeLotes(item.lotes),
    necesita_refrigeracion: Boolean(item.necesita_refrigeracion)
});

const rethrowApiError = (error, fallback) => {
    if (isNetworkOrOfflineError(error)) {
        throw error;
    }

    throw new Error(getApiErrorMessage(error, fallback));
};

/** Inventario usa paginación por `page` (no `offset`). */
export const normalizeInventoryListParams = (params = {}) => {
    const next = {...params};
    const limit = Math.min(Math.max(Number(next.limit) || 10, 1), 500);

    next.limit = limit;

    if (next.page === undefined) {
        if (next.offset !== undefined) {
            next.page = Math.floor(Number(next.offset) / limit) + 1;
            delete next.offset;
        } else {
            next.page = 1;
        }
    } else {
        delete next.offset;
    }

    if (!String(next.search || '').trim()) {
        delete next.search;
    }

    if (next.proveedor_id === '' || next.proveedor_id === null || next.proveedor_id === undefined) {
        delete next.proveedor_id;
    }

    if (next.include_lotes === undefined || next.include_lotes === null || next.include_lotes === '' || next.include_lotes === false) {
        delete next.include_lotes;
    } else if (next.include_lotes === true) {
        next.include_lotes = 1;
    }

    return next;
};

const unwrapInventoryListResponse = raw => {
    if (Array.isArray(raw)) {
        return {rows: raw, pagination: null};
    }

    return {
        rows: Array.isArray(raw?.data) ? raw.data : [],
        pagination: raw?.pagination || null
    };
};

const sortInventoryRows = (rows, sort = 'id', order = 'asc') => {
    const dir = order === 'desc' ? -1 : 1;

    return [...rows].sort((a, b) => {
        let av;
        let bv;

        switch (sort) {
            case 'nombre':
                av = a.nombre;
                bv = b.nombre;
                break;
            case 'modelo':
                av = a.modelo;
                bv = b.modelo;
                break;
            case 'cantidad':
                av = Number(a.cantidad);
                bv = Number(b.cantidad);
                return (av - bv) * dir;
            default:
                av = a.id;
                bv = b.id;
        }

        return String(av ?? '').localeCompare(String(bv ?? ''), 'es', {numeric: true, sensitivity: 'base'}) * dir;
    });
};

const paginateStoredInventory = (rows, params = {}) => {
    const listParams = normalizeInventoryListParams(params);
    const filtered = applyInventoryListFilters(rows, params);
    const sorted = sortInventoryRows(filtered, listParams.sort, listParams.order);
    const total = sorted.length;
    const limit = listParams.limit;
    const page = listParams.page;
    const start = (page - 1) * limit;

    return {
        data: sorted.slice(start, start + limit),
        pagination: {
            total,
            page,
            limit,
            totalPages: Math.max(1, Math.ceil(total / limit))
        }
    };
};

/** Cuando GET /inventario/:id falla (p. ej. join de catálogo), el listado suele seguir disponible. */
const fetchItemFromInventoryList = async id => {
    try {
        const {data} = await inventoryAPI.get('/inventario', {
            params: normalizeInventoryListParams({limit: 500, page: 1})
        });
        const {rows} = unwrapInventoryListResponse(data);
        const match = rows.find(row => String(row.id) === String(id));

        return match ? normalizeItem(match) : null;
    } catch {
        return null;
    }
};

const applyInventoryListFilters = (rows, params = {}) => {
    const search = String(params?.search || '').trim().toLowerCase();
    const proveedorId = params?.proveedor_id;

    return rows.filter(item => {
        if (proveedorId !== undefined && proveedorId !== null && proveedorId !== '') {
            if (String(item.proveedor_id) !== String(proveedorId)) {
                return false;
            }
        }

        if (!search) {
            return true;
        }

        const haystack = [
            item.nombre,
            item.modelo,
            item.tipo?.nombre,
            item.proveedor?.nombre,
            ...(Array.isArray(item.proveedores) ? item.proveedores.map(p => p?.nombre) : [])
        ]
            .join(' ')
            .toLowerCase();

        return haystack.includes(search);
    });
};

/** @returns {{ data: object[], pagination: object|null }} */
export const getInventoryListResult = result => {
    if (Array.isArray(result)) {
        return {data: result, pagination: null};
    }

    return {
        data: Array.isArray(result?.data) ? result.data : [],
        pagination: result?.pagination || null
    };
};

export const inventoryService = {
    async getAll(params = {}) {
        try {
            const {data} = await inventoryAPI.get('/inventario', {params: normalizeInventoryListParams(params)});
            const {rows, pagination} = unwrapInventoryListResponse(data);

            return {
                data: rows.map(normalizeItem),
                pagination
            };
        } catch (error) {
            if (!isNetworkOrOfflineError(error)) {
                rethrowApiError(error, 'No se pudo cargar el inventario.');
            }

            return paginateStoredInventory(getStoredInventory().map(normalizeItem), params);
        }
    },

    async getById(id) {
        try {
            const {data} = await inventoryAPI.get(`/inventario/${id}`);
            return normalizeItem(data?.data || data);
        } catch (error) {
            if (!isNetworkOrOfflineError(error)) {
                const fromList = await fetchItemFromInventoryList(id);
                if (fromList) {
                    return fromList;
                }

                rethrowApiError(error, 'No se pudo cargar el producto.');
            }

            const rows = getStoredInventory();
            const item = rows.find(row => String(row.id) === String(id));

            if (!item) {
                throw new Error('Producto no encontrado');
            }

            return normalizeItem(item);
        }
    },

    async create(payload) {
        if (payload?.fecha_caducidad && isDateBeforeToday(String(payload.fecha_caducidad).slice(0, 10))) {
            throw new Error('La fecha de caducidad no puede ser anterior a hoy.');
        }

        try {
            const {data} = await inventoryAPI.post('/inventario', stripTenantFromPayload(payload));
            return normalizeItem(data?.data || data);
        } catch (error) {
            if (!isNetworkOrOfflineError(error)) {
                rethrowApiError(error, 'No se pudo registrar el producto.');
            }

            const rows = getStoredInventory();
            const newItem = {
                id: `inv-${Date.now()}`,
                ...payload,
                cantidad: Number(payload.cantidad || 0),
                limite_recompra: payload.limite_recompra === null || payload.limite_recompra === undefined || payload.limite_recompra === ''
                    ? ''
                    : Number(payload.limite_recompra),
                precio_por_unidad: payload.precio_por_unidad === null || payload.precio_por_unidad === undefined
                    ? ''
                    : Number(payload.precio_por_unidad)
            };

            setStoredInventory([newItem, ...rows]);
            return newItem;
        }
    },

    async update(id, payload) {
        if (payload?.fecha_caducidad && isDateBeforeToday(String(payload.fecha_caducidad).slice(0, 10))) {
            throw new Error('La fecha de caducidad no puede ser anterior a hoy.');
        }

        try {
            const {data} = await inventoryAPI.put(`/inventario/${id}`, payload);
            return normalizeItem(data?.data || data);
        } catch (error) {
            if (!isNetworkOrOfflineError(error)) {
                rethrowApiError(error, 'No se pudo actualizar el producto.');
            }

            const rows = getStoredInventory();
            const updatedRows = rows.map(row =>
                String(row.id) === String(id)
                    ? {
                        ...row,
                        ...payload,
                        cantidad: Number(payload.cantidad || 0),
                        limite_recompra: payload.limite_recompra === null || payload.limite_recompra === undefined || payload.limite_recompra === ''
                            ? ''
                            : Number(payload.limite_recompra),
                        precio_por_unidad: payload.precio_por_unidad === null || payload.precio_por_unidad === undefined
                            ? ''
                            : Number(payload.precio_por_unidad)
                    }
                    : row
            );

            setStoredInventory(updatedRows);
            return updatedRows.find(row => String(row.id) === String(id));
        }
    },

    async remove(id) {
        try {
            const {data} = await inventoryAPI.delete(`/inventario/${id}`);
            return data;
        } catch (error) {
            if (!isNetworkOrOfflineError(error)) {
                rethrowApiError(error, 'No se pudo eliminar el producto.');
            }

            const rows = getStoredInventory();
            const updatedRows = rows.filter(row => String(row.id) !== String(id));
            setStoredInventory(updatedRows);
            return {success: true};
        }
    },

    async getTipos() {
        try {
            const {data} = await inventoryAPI.get('/inventario/tipos');
            const rows = Array.isArray(data) ? data : (data?.data || []);
            return rows.map(normalizeType);
        } catch (error) {
            return getStoredTypes().map(normalizeType);
        }
    },

    async getTipoById(id) {
        try {
            const {data} = await inventoryAPI.get(`/inventario/tipos/${id}`);
            return normalizeType(data?.data || data);
        } catch (error) {
            const rows = getStoredTypes();
            const type = rows.find(row => String(row.id) === String(id));

            if (!type) {
                throw new Error('Tipo no encontrado');
            }

            return normalizeType(type);
        }
    },

    async createTipo(payload) {
        try {
            const {data} = await inventoryAPI.post('/inventario/tipos', payload);
            return normalizeType(data?.data || data);
        } catch (error) {
            const rows = getStoredTypes();
            const newType = {
                id: Date.now(),
                nombre: payload.nombre,
                campos: payload.campos || null,
                limite_recompra: payload.limite_recompra === null || payload.limite_recompra === undefined || payload.limite_recompra === ''
                    ? 0
                    : Number(payload.limite_recompra)
            };
            setStoredTypes([...rows, newType]);
            return normalizeType(newType);
        }
    },

    async updateTipo(id, payload) {
        try {
            const {data} = await inventoryAPI.put(`/inventario/tipos/${id}`, payload);
            return normalizeType(data?.data || data);
        } catch (error) {
            const rows = getStoredTypes();
            const updatedRows = rows.map(row =>
                Number(row.id) === Number(id) ? {...row, ...payload} : row
            );
            setStoredTypes(updatedRows);
            return normalizeType(updatedRows.find(row => Number(row.id) === Number(id)));
        }
    },

    async deleteTipo(id) {
        try {
            const {data} = await inventoryAPI.delete(`/inventario/tipos/${id}`);
            return data;
        } catch (error) {
            const rows = getStoredTypes();
            const updatedRows = rows.filter(row => Number(row.id) !== Number(id));
            setStoredTypes(updatedRows);
            return {success: true};
        }
    }
};
